import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BACKEND_URL } from '../../config';

const STEPS = [
  { key: 'service', label: 'What do you need help with?', placeholder: 'e.g. Leaking tap, boiler repair, blocked drain', type: 'textarea' },
  { key: 'location', label: 'Where are you located?', placeholder: 'e.g. 10 High Street, Manchester', type: 'text' },
  { key: 'preferred_time', label: 'When do you need this?', placeholder: 'e.g. This week, ASAP, mornings preferred', type: 'text' },
  { key: 'customer_name', label: 'Your name', placeholder: '', type: 'text' },
  { key: 'customer_phone', label: 'Phone number', placeholder: '', type: 'tel' },
  { key: 'customer_email', label: 'Email address', placeholder: '', type: 'email' },
];

export default function PublicEnquiryPage() {
  const { publicKey } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState('');

  const [form, setForm] = useState({
    service: '', location: '', preferred_time: '',
    customer_name: '', customer_phone: '', customer_email: '',
  });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/service-os/public/enquiry/${publicKey}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(setConfig)
      .catch(() => setError('This enquiry form is not available.'))
      .finally(() => setLoading(false));
  }, [publicKey]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/service-os/public/enquiry/${publicKey}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_needed: form.service,
          location: form.location,
          preferred_time: form.preferred_time,
          customer_name: form.customer_name,
          customer_phone: form.customer_phone,
          customer_email: form.customer_email,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setConfirmMsg(data.message);
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const brandColor = config?.brand_color || '#14F195';
  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const val = form[currentStep?.key] || '';
  const required = ['service', 'customer_name', 'customer_email'];
  const canNext = currentStep ? (required.includes(currentStep.key) ? val.trim().length > 0 : true) : false;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: brandColor, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4">
        <p className="text-[#8B8FA3] text-center" data-testid="enquiry-error">{error}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4" data-testid="enquiry-confirmed">
        <div className="w-full max-w-md text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${brandColor}20` }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={brandColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Enquiry received</h2>
          <p className="text-sm text-[#C4C9D4]">{confirmMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4" data-testid="enquiry-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-lg font-bold text-white" data-testid="enquiry-business-name">{config.business_name}</h1>
          {config.intro_text ? (
            <p className="text-sm text-[#8B8FA3] mt-1">{config.intro_text}</p>
          ) : (
            <p className="text-sm text-[#8B8FA3] mt-1">Tell us what you need and we'll get back to you.</p>
          )}
          {config.services_offered && (
            <p className="text-xs text-[#555] mt-2">Services: {config.services_offered}</p>
          )}
        </div>

        {/* Progress */}
        <div className="flex gap-1 mb-6">
          {STEPS.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full transition-colors" style={{ backgroundColor: i <= step ? brandColor : '#1E1F2E' }} />
          ))}
        </div>

        {/* Step form */}
        <div className="bg-[#12131A] border border-[#1E1F2E] rounded-xl p-6">
          <label className="block text-sm font-medium text-[#E2E8F0] mb-3">
            {currentStep.label}
            {!required.includes(currentStep.key) && <span className="text-[#555] font-normal ml-1">(optional)</span>}
          </label>

          {currentStep.type === 'textarea' ? (
            <textarea
              rows={3}
              value={val}
              onChange={e => setForm(f => ({ ...f, [currentStep.key]: e.target.value }))}
              placeholder={currentStep.placeholder}
              className="w-full px-4 py-3 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none placeholder:text-[#555]"
              style={{ '--tw-ring-color': brandColor }}
              autoFocus
              data-testid={`enquiry-field-${currentStep.key}`}
            />
          ) : (
            <input
              type={currentStep.type}
              value={val}
              onChange={e => setForm(f => ({ ...f, [currentStep.key]: e.target.value }))}
              placeholder={currentStep.placeholder}
              className="w-full px-4 py-3 bg-[#0B0C10] border border-[#1E1F2E] rounded-lg text-white text-sm focus:outline-none placeholder:text-[#555]"
              autoFocus
              data-testid={`enquiry-field-${currentStep.key}`}
              onKeyDown={e => {
                if (e.key === 'Enter' && canNext) {
                  e.preventDefault();
                  isLast ? handleSubmit() : setStep(step + 1);
                }
              }}
            />
          )}

          {error && <p className="mt-2 text-sm text-red-400">{error}</p>}

          <div className="flex justify-between mt-5">
            {step > 0 ? (
              <button onClick={() => { setStep(step - 1); setError(''); }} className="text-sm text-[#8B8FA3] hover:text-white transition-colors">
                Back
              </button>
            ) : <div />}
            {isLast ? (
              <button
                onClick={handleSubmit}
                disabled={!canNext || submitting}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-[#0B0C10] disabled:opacity-40 transition-colors"
                style={{ backgroundColor: brandColor }}
                data-testid="enquiry-submit-btn"
              >
                {submitting ? 'Sending...' : 'Send enquiry'}
              </button>
            ) : (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canNext}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-[#0B0C10] disabled:opacity-40 transition-colors"
                style={{ backgroundColor: brandColor }}
                data-testid="enquiry-next-btn"
              >
                Next
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-[#555] mt-4">
          Powered by {config.business_name}
        </p>
      </div>
    </div>
  );
}
